import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { PaiementBox } from '../components/DOMIUMAssistantDevis/PaiementBox';
import axios from 'axios';
import { FileText, Download, Loader2, ShieldCheck, CheckCircle2, PenLine } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/domium-ia/devis-public`;
const fmt = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`;

export default function DevisConsultationPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [dl, setDl] = useState(null);
  const [params] = useSearchParams();
  const reload = useCallback(() => axios.get(`${API}/${token}`).then((r) => setData(r.data)), [token]);
  const [sign, setSign] = useState({ step: 'idle', email: '', code: '', accept: false, busy: false, error: '', canal: 'sms' });
  const [canaux, setCanaux] = useState({ sms: null, email: null });
  useEffect(() => {
    axios.get(`${API}/${token}/signature/canaux`).then((r) => {
      setCanaux(r.data);
      if (!r.data.sms) setSign((s) => ({ ...s, canal: 'email' }));
    }).catch(() => {});
  }, [token]);
  const patch = (p) => setSign((s) => ({ ...s, ...p }));

  const requestCode = async () => {
    patch({ busy: true, error: '' });
    try { const r = await axios.post(`${API}/${token}/signature/request`, { canal: sign.canal }); patch({ step: 'code', email: r.data.destination || r.data.email_masque }); }
    catch (e) { patch({ error: e.response?.data?.detail || "Envoi du code impossible" }); }
    finally { patch({ busy: false }); }
  };
  const validate = async (e) => {
    e.preventDefault();
    patch({ busy: true, error: '' });
    try { await axios.post(`${API}/${token}/signature/validate`, { code: sign.code }); patch({ step: 'done' }); setData((d) => ({ ...d, etat: 'signe' })); }
    catch (err) { patch({ error: err.response?.data?.detail || 'Code invalide' }); }
    finally { patch({ busy: false }); }
  };

  useEffect(() => {
    axios.get(`${API}/${token}`).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || 'Lien invalide ou expiré'));
  }, [token]);

  const open = async (kind) => {
    setDl(kind);
    try {
      const r = await axios.get(`${API}/${token}/${kind}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 300000);
    } catch { setError('Document indisponible pour le moment'); } finally { setDl(null); }
  };

  return (
    <main className="min-h-screen bg-slate-50 flex items-start justify-center py-16 px-4" data-testid="devis-consultation-page">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-sm border border-slate-200 p-8 space-y-5">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-8 w-8 text-violet-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="mon-dossier-titre">Mon dossier DOS-DOMIUM</h1>
            <p className="text-sm text-slate-500">O'SCOP Outremer — COPPAM Garanties Solidaires</p>
          </div>
        </div>
        {error && <p className="text-sm text-red-600 bg-red-50 rounded p-3" data-testid="consultation-error">{error}</p>}
        {!data && !error && <Loader2 className="h-6 w-6 animate-spin text-violet-500" />}
        {data && (
          <>
            <p className="text-sm text-slate-600">Dossier <strong data-testid="consultation-reference">{data.reference}</strong>{data.demandeur && <> — {data.demandeur}</>}</p>
            {data.projet && <p className="text-xs text-slate-500 bg-slate-50 rounded p-3">{data.projet}</p>}
            <p className="text-lg font-semibold text-emerald-700" data-testid="consultation-montant">Contribution : {fmt(data.contribution_ttc)} TTC</p>
            {data.etat === 'signe' && <p className="flex items-center gap-2 text-emerald-700 text-sm"><CheckCircle2 className="h-4 w-4" /> Ce devis a été signé. Merci !</p>}
            <div className="space-y-2">
              {data.documents.map((d) => (
                <Button key={d.kind} variant="outline" onClick={() => open(d.kind)} disabled={!!dl} className="w-full justify-between" data-testid={`consultation-doc-${d.kind}`}>
                  <span className="flex items-center gap-2"><FileText className="h-4 w-4 text-violet-500" /> {d.label}</span>
                  {dl === d.kind ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                </Button>
              ))}
            </div>
            {data.etat !== 'signe' && (
              <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-4 space-y-3" data-testid="consultation-sign-box">
                <h2 className="flex items-center gap-2 font-semibold text-violet-900"><PenLine className="h-4 w-4" /> Signer mon devis en ligne</h2>
                {sign.step === 'idle' && (
                  <>
                    <label className="flex items-start gap-2 text-sm text-slate-700">
                      <input type="checkbox" checked={sign.accept} onChange={(e) => patch({ accept: e.target.checked })} className="mt-1" data-testid="consultation-sign-accept" />
                      <span>J'ai lu le devis ESS et le dossier maître (Conditions Particulières et Générales). Lu et approuvé — Bon pour accord et engagement coopératif.</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2" data-testid="consultation-sign-canal">
                      {[['sms', 'Code par SMS', canaux.sms], ['email', 'Code par email', canaux.email]].map(([k, label, dest]) => (
                        <button key={k} type="button" disabled={!dest} onClick={() => patch({ canal: k })}
                          className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${sign.canal === k ? 'border-violet-600 bg-violet-100 text-violet-900' : 'border-slate-200 bg-white text-slate-700'} disabled:opacity-40`}
                          data-testid={`consultation-sign-canal-${k}`}>
                          <span className="block font-semibold">{label}</span>
                          <span className="block text-[11px] text-slate-500">{dest || 'non disponible'}</span>
                        </button>
                      ))}
                    </div>
                    <Button onClick={requestCode} disabled={!sign.accept || sign.busy} className="w-full bg-violet-600 hover:bg-violet-700 text-white" data-testid="consultation-sign-btn">
                      {sign.busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PenLine className="h-4 w-4 mr-2" />} Signer mon devis
                    </Button>
                    <p className="text-[11px] text-slate-500">Un code de vérification à usage unique vous sera envoyé par {sign.canal === 'sms' ? 'SMS' : 'email'} (signature électronique simple, horodatée).</p>
                  </>
                )}
                {sign.step === 'code' && (
                  <form onSubmit={validate} className="space-y-2">
                    <p className="text-sm text-slate-700">Un code vient d'être envoyé à <strong>{sign.email}</strong>. Saisissez-le pour signer.</p>
                    <Input value={sign.code} onChange={(e) => patch({ code: e.target.value.replace(/\D/g, '').slice(0, 6) })} inputMode="numeric" placeholder="Code à 6 chiffres" className="text-center text-lg tracking-widest" data-testid="consultation-sign-code" />
                    <Button type="submit" disabled={sign.code.length < 6 || sign.busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="consultation-sign-validate-btn">
                      {sign.busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Valider ma signature
                    </Button>
                    <button type="button" onClick={requestCode} disabled={sign.busy} className="text-xs text-violet-700 underline" data-testid="consultation-sign-resend">Renvoyer un code</button>
                  </form>
                )}
                {sign.error && <p className="text-sm text-red-600" data-testid="consultation-sign-error">{sign.error}</p>}
              </div>
            )}
            {data.etat === 'signe' && <PaiementBox api={API} token={token} data={data} params={params} onPaid={reload} />}
            {sign.step === 'done' && <p className="text-sm text-emerald-700 bg-emerald-50 rounded p-3" data-testid="consultation-sign-success">Votre signature a bien été enregistrée. Vous recevrez une confirmation par email.</p>}
          </>
        )}
      </div>
    </main>
  );
}
